import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  fetchVendorBanners,
  createVendorBanner,
  updateVendorBanner,
  deleteVendorBanner,
  type VendorBanner,
} from '@/lib/marketplaceCatalog';
import type { AnnonceItem } from './VendorProductForm';

interface VendorBannersTabProps {
  marketplaceId?: number;
  vendorId: number;
  annonces: AnnonceItem[];
}

interface BannerForm {
  idannonce: string;
  titre: string;
  description: string;
  image: string;
}

const EMPTY_FORM: BannerForm = { idannonce: '', titre: '', description: '', image: '' };

/**
 * Bannières publicitaires du vendeur. Chaque bannière est rattachée à une
 * de ses annonces et s'affiche sur la page « boutique » de son vendeur
 * dans la marketplace (jamais sur l'accueil, réservé au propriétaire).
 */
export default function VendorBannersTab({ marketplaceId, vendorId, annonces }: VendorBannersTabProps) {
  const [banners, setBanners] = useState<VendorBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<VendorBanner | null>(null);
  const [form, setForm] = useState<BannerForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<VendorBanner | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Annonces publiées que le vendeur peut mettre en avant via une bannière.
  const publishedAnnonces = annonces.filter((a) => a.publique === 1);
  const annonceById = new Map(annonces.map((a) => [Number(a.id), a]));

  const load = useCallback(async () => {
    if (!marketplaceId || !vendorId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchVendorBanners(marketplaceId, vendorId);
      setBanners(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [marketplaceId, vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (b: VendorBanner) => {
    setEditing(b);
    setForm({
      idannonce: b.idannonce ? String(b.idannonce) : '',
      titre: b.titre,
      description: b.description,
      image: b.image,
    });
    setError('');
    setShowForm(true);
  };

  const selectAnnonce = (value: string) => {
    const found = annonceById.get(Number(value));
    setForm((f) => ({
      ...f,
      idannonce: value,
      // Pré-remplit titre / image depuis l'annonce choisie si vides.
      titre: f.titre.trim() ? f.titre : (found?.titre || ''),
      image: f.image.trim() ? f.image : (found?.image || ''),
    }));
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadMediaFile(file, 'banners');
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'upload de l'image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!marketplaceId) {
      setError('Marketplace introuvable.');
      return;
    }
    if (!form.titre.trim() && !form.image.trim()) {
      setError('Ajoutez au moins un titre ou une image à la bannière.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        titre: form.titre.trim(),
        description: form.description.trim(),
        image: form.image.trim(),
        idannonce: form.idannonce ? Number(form.idannonce) : 0,
      };
      if (editing) {
        await updateVendorBanner(editing.id, payload);
      } else {
        await createVendorBanner({ marketplaceId, vendorId, ...payload });
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    setBanners((prev) => prev.filter((b) => b.id !== target.id));
    try {
      await deleteVendorBanner(target.id);
    } catch {
      setError('Impossible de supprimer la bannière.');
      await load();
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-xs text-foreground-500 flex items-start gap-2 max-w-xl">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          Créez des bannières rattachées à vos annonces. Elles s&apos;affichent sur la page de votre boutique dans la marketplace.
        </p>
        <button
          type="button"
          onClick={openCreate}
          disabled={publishedAnnonces.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap w-fit disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-add-line"></i>
          Nouvelle bannière
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : publishedAnnonces.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-shopping-bag-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-900 mb-1">Publiez d&apos;abord une annonce</h3>
          <p className="text-sm text-foreground-500 max-w-xs">
            Créez et publiez au moins une annonce dans l&apos;onglet « Mes produits » pour pouvoir créer une bannière.
          </p>
        </div>
      ) : banners.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-image-2-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-900 mb-1">Aucune bannière</h3>
          <p className="text-sm text-foreground-500 max-w-xs">
            Mettez en avant une de vos annonces avec une bannière visible sur votre boutique.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {banners.map((b) => {
            const img = resolveMediaUrl(b.image);
            const linked = b.idannonce ? annonceById.get(b.idannonce) : null;
            return (
              <div key={b.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="relative h-40 bg-background-100">
                  {img ? (
                    <img src={img} alt={b.titre || 'Bannière'} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-3xl text-foreground-300"></i>
                    </div>
                  )}
                  {b.idannonce > 0 && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-50/90 text-foreground-700 flex items-center gap-1">
                      <i className="ri-links-line"></i>
                      Rattachée à une annonce
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate">{b.titre || 'Bannière'}</h3>
                  {b.description && <p className="text-xs text-foreground-500 line-clamp-1 mt-0.5">{b.description}</p>}
                  {linked && (
                    <p className="text-[11px] text-foreground-400 mt-1 truncate">
                      <i className="ri-megaphone-line mr-1"></i>
                      Vers « {linked.titre} »
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-background-200/70">
                    <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                      <i className="ri-cursor-line"></i>
                      {b.click} clic{b.click > 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(b)}
                        className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
                        title="Modifier"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(b)}
                        className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                        title="Supprimer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulaire */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : () => setShowForm(false)} />
          <form
            onSubmit={handleSubmit}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold font-heading text-foreground-950">
                {editing ? 'Modifier la bannière' : 'Nouvelle bannière'}
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                  Annonce à rattacher
                </label>
                <select
                  value={form.idannonce}
                  onChange={(e) => selectAnnonce(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Aucune (bannière simple)</option>
                  {publishedAnnonces.map((a) => (
                    <option key={a.id} value={a.id}>{a.titre}</option>
                  ))}
                </select>
                <p className="text-[11px] text-foreground-400 mt-1">
                  Un clic sur la bannière ouvrira l&apos;annonce choisie sur la marketplace.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Titre</label>
                <input
                  type="text"
                  value={form.titre}
                  onChange={(e) => setForm((f) => ({ ...f, titre: e.target.value }))}
                  maxLength={90}
                  placeholder="Ex : Promotion de la semaine"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description courte</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  maxLength={180}
                  placeholder="Ex : Jusqu'à -30% cette semaine !"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Image de la bannière</label>
                <div className="flex items-center gap-3">
                  {form.image ? (
                    <div className="w-24 h-16 rounded-lg overflow-hidden border border-background-200/70 flex-shrink-0 bg-background-100">
                      <img src={resolveMediaUrl(form.image)} alt="" className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-24 h-16 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-foreground-300"></i>
                    </div>
                  )}
                  <div className="flex-1">
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="px-3.5 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
                    >
                      <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                      {uploading ? 'Upload...' : form.image ? "Changer l'image" : 'Choisir une image'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                  ) : (
                    <><i className="ri-save-line"></i>{editing ? 'Enregistrer' : 'Créer'}</>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation suppression */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleting(null)} />
          <div className="relative w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer la bannière ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Cette action est irréversible.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}